<div class="container mx-auto p-4">
  <h1 class="text-2xl font-bold mb-4">{{ 'APPLICATION_SETTINGS.TITLE' | translate }}</h1>

  <!-- Error message -->
  @if (error) {
    <app-alert [type]="'danger'">{{ error }}</app-alert>
  }

  <!-- Success message -->
  @if (success) {
    <app-alert [type]="'success'">{{ success }}</app-alert>
  }

  <!-- Action buttons -->
  <div class="flex justify-end mb-4 space-x-3">
    <app-button icon="save" color="primary"
                (click)="discardChanges()"
                [disabled]="changedSettings.length === 0"
                [ngClass]="{'opacity-50 cursor-not-allowed': changedSettings.length === 0}">
      {{ 'APPLICATION_SETTINGS.DISCARD_CHANGES' | translate }}
    </app-button>
    <app-button icon="x" color="danger"
                (click)="openConfirmModal()"
                [disabled]="changedSettings.length === 0"
                [ngClass]="{'opacity-50 cursor-not-allowed': changedSettings.length === 0}">
      {{ 'APPLICATION_SETTINGS.SAVE_CHANGES' | translate }} ({{ changedSettings.length }})
    </app-button>
  </div>

  <!-- Settings list -->
  <div class="shadow-md overflow-auto">
    <table class="table-auto min-w-full border-collapse border border-slate-400">
      <thead>
      <tr class="bg-neutral-100 dark:bg-neutral-700">
        <th class="border border-neutral-300 dark:border-neutral-600 p-2">{{ 'APPLICATION_SETTINGS.KEY' | translate }}</th>
        <th class="border border-neutral-300 dark:border-neutral-600 p-2">{{ 'APPLICATION_SETTINGS.VALUE' | translate }}</th>
        <th class="border border-neutral-300 dark:border-neutral-600 p-2">{{ 'APPLICATION_SETTINGS.SENSITIVE' | translate }}</th>
        <th class="border border-neutral-300 dark:border-neutral-600 p-2">{{ 'APPLICATION_SETTINGS.ACTIONS' | translate }}</th>
      </tr>
      </thead>
      <tbody class="divide-y divide-gray-200">
      @for (setting of settings; let even = $even; track setting.key) {
        <tr [ngClass]="isSettingChanged(setting.key) ? 'bg-blue-50 dark:bg-blue-900/20' : (even ? 'bg-neutral-50 dark:bg-neutral-800' : 'bg-white dark:bg-neutral-900')">
          <td class="border border-neutral-300 dark:border-neutral-600 p-2">
            <p>{{ setting.key }}</p>
            <span class="text-xs text-gray-500">
              {{ `APPLICATION_SETTINGS.DESCRIPTIONS.${setting.key}` | translate }}
            </span>
          </td>
          <td class="border border-neutral-300 dark:border-neutral-600 p-2">
            <!-- Non-sensitive value or not editing -->
            @if (!setting.isSensitiveData && !editingKeys.has(setting.key)) {
              <span>{{ setting.value.length > 64 ? setting.value.substring(0, 64) + '...' : setting.value }}</span>
            }

            <!-- Sensitive value and not editing -->
            @if (setting.isSensitiveData && !editingKeys.has(setting.key)) {
              <span>********</span>
            }

            <!-- Editing mode -->
            @if (editingKeys.has(setting.key)) {
              <div class="flex align-middle items-center gap-3">
                <input type="text"
                       [(ngModel)]="setting.value"
                       (ngModelChange)="onSettingChange(setting)"
                       class="shadow appearance-none border rounded w-full py-1 px-2 leading-tight focus:outline-none focus:shadow-outline">
                <app-button icon="x" color="danger" size="small" (click)="cancelEditing(setting)"></app-button>
              </div>
            }
          </td>
          <td class="border border-neutral-300 dark:border-neutral-600 p-2">
            <div class="flex items-center">
              @if (isSuperAdministrator) {
                <input type="checkbox"
                       [(ngModel)]="setting.isSensitiveData"
                       (ngModelChange)="onSettingChange(setting)"
                       class="form-checkbox h-4 w-4 text-blue-600 mr-2">
              }

              @if (setting.isSensitiveData) {
                <app-badge type="success" icon="shield" [iconSize]="16">Sim</app-badge>
              } @else {
                <app-badge type="info" icon="shield-off" [iconSize]="16">Não</app-badge>
              }
            </div>
          </td>
          <td class="border border-neutral-300 dark:border-neutral-600 p-2 text-left text-sm font-medium">
            <app-button [disabled]="loading" (click)="startEditing(setting)" color="primary" icon="edit"></app-button>
          </td>
        </tr>
      } @empty {
        <tr>
          @if (loading) {
            <td colspan="4" class="p-8 text-sm text-neutral-600">
              <app-loading size="large" color="primary"/>
            </td>
          } @else {
            <td colspan="4" class="p-3 text-sm">Nenhum resultado.</td>
          }
        </tr>
      }
      </tbody>
    </table>
  </div>
</div>

<!-- Confirmation Modal -->
<app-modal #changeSecretConfirmationModal [title]="'APPLICATION_SETTINGS.CONFIRM_MODAL.TITLE' | translate">
  <p class="mb-4">{{ 'APPLICATION_SETTINGS.CONFIRM_MODAL.DESCRIPTION' | translate }}</p>

  <div class="max-h-60 overflow-y-auto mb-4">
    <table class="min-w-full divide-y divide-gray-200">
      <thead class="bg-gray-50 dark:bg-gray-700">
      <tr>
        <th class="border border-neutral-300 dark:border-neutral-600 p-2">{{ 'APPLICATION_SETTINGS.KEY' | translate }}</th>
        <th class="border border-neutral-300 dark:border-neutral-600 p-2">{{ 'APPLICATION_SETTINGS.CONFIRM_MODAL.ORIGINAL_VALUE' | translate }}</th>
        <th class="border border-neutral-300 dark:border-neutral-600 p-2">{{ 'APPLICATION_SETTINGS.CONFIRM_MODAL.NEW_VALUE' | translate }}</th>
        <th class="border border-neutral-300 dark:border-neutral-600 p-2">{{ 'APPLICATION_SETTINGS.SENSITIVE' | translate }}</th>
      </tr>
      </thead>
      <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
      @for (setting of changedSettings;  track setting.key) {
        <tr>
          <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">{{ setting.key }}</td>
          <td class="px-6 py-4 whitespace-nowrap text-sm">
            @for (orig of originalSettings; track orig.key) {
              @if (orig.key === setting.key) {
                <span>{{ orig.value }}</span>
              }
            }
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm">
            <span>{{ setting.value }}</span>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm">
            @if (setting.isSensitiveData) {
              <app-badge type="success" icon="shield" [iconSize]="16">Sim</app-badge>
            } @else {
              <app-badge type="info" icon="shield-off" [iconSize]="16">Não</app-badge>
            }
          </td>
        </tr>
      }
      </tbody>
    </table>
  </div>

  <div class="flex justify-end space-x-3">
    <app-button type="button"
                color="primary"
                [disabled]="loading"
                [ngClass]="{'opacity-50 cursor-not-allowed': loading}"
                [loading]="loading"
                (click)="saveChanges()">
      {{ 'APPLICATION_SETTINGS.CONFIRM_MODAL.CONFIRM' | translate }}
    </app-button>
    <app-button type="button" color="danger" (click)="confirmModal.closeDialog()">
      {{ 'APPLICATION_SETTINGS.CONFIRM_MODAL.CANCEL' | translate }}
    </app-button>
  </div>
</app-modal>
